<template>
  <ResponsivePanel
    v-model="isOpen"
    side="bottom"
    align="start"
    :sideOffset
    inheritWidth
    :title="label"
    :disabled="disabled"
  >
    <template #trigger>
      <div class="PassengersInput" :class="{ 'is-disabled': disabled, 'is-error': hasError }">
        <FieldLabel
          v-if="label"
          :for="name"
          :required="isRequired"
          :hideAsterisk
          :disabled
        >
          {{ label }}
        </FieldLabel>

        <button
          :id="name"
          type="button"
          class="PassengersInput-field"
          :name="name"
          :disabled="disabled"
        >
          <div class="PassengersInput-content">
            <div v-if="triggerSelectedPassengers.length" class="PassengersInput-avatars">
              <Avatar
                v-for="passenger of triggerSelectedPassengers"
                :key="passenger.person.id"
                class="PassengersInput-avatar"
                :name="getPersonFullName(passenger.person)"
                size="sm"
                active
              />
            </div>

            <span v-else class="PassengersInput-placeholder">{{ placeholder }}</span>
          </div>

          <Icon icon="chevron-down" :size="20" class="PassengersInput-chevron" />
        </button>

        <FieldHelper :errorMessage="errorMessage" :helperText />
      </div>
    </template>

    <template v-if="isMobile" #title>
      <div class="PassengersInput-drawerHeader">
        <span class="PassengersInput-drawerHeaderLabel">{{ label }}</span>

        <div v-if="activePassengers.length" class="PassengersInput-avatars">
          <Avatar
            v-for="passenger of activePassengers"
            :key="passenger.person.id"
            class="PassengersInput-avatar"
            :name="getPersonFullName(passenger.person)"
            size="sm"
            active
          />
        </div>
      </div>
    </template>

    <div class="PassengersInput-panel">
      <Status
        v-for="statusMessage of statusMessages"
        :key="statusMessage.code"
        :status="statusMessage.status"
        persistent
        class="PassengersInput-status"
      >
        {{ statusMessage.message }}
      </Status>

      <div v-if="activePassengers.length" class="PassengersInput-section">
        <p class="PassengersInput-sectionTitle">
          {{ t('ui.booking.search.passengers.alreadyTraveling') }}
        </p>

        <TransitionGroup
          name="PassengersInput-list"
          appear
          tag="div"
          class="PassengersInput-list"
        >
          <PassengerItem
            v-for="passenger of activePassengers"
            :key="passenger.person.id"
            :passenger="passenger"
            selected
            :showSeatSelection
            :tripType
            :seatSelection="activeSeatTypes[passenger.person.id]"
            @toggle="togglePassenger(passenger.person.id)"
            @update:seat-selection="setSeatSelection(passenger.person.id, $event)"
          />
        </TransitionGroup>
      </div>

      <div class="PassengersInput-section">
        <p class="PassengersInput-sectionTitle">
          {{ t('ui.booking.search.passengers.addPassengers') }}
        </p>

        <TransitionGroup
          v-if="availablePassengers.length"
          name="PassengersInput-list"
          appear
          tag="div"
          class="PassengersInput-list"
        >
          <PassengerItem
            v-for="passenger of availablePassengers"
            :key="passenger.person.id"
            :passenger="passenger"
            :disabled="isMaxReached"
            :showSeatSelection
            :tripType
            :seatSelection="activeSeatTypes[passenger.person.id]"
            @toggle="togglePassenger(passenger.person.id)"
            @update:seat-selection="setSeatSelection(passenger.person.id, $event)"
          />
        </TransitionGroup>

        <div class="PassengersInput-emptyState">
          <p v-show="noPassengersAvailable" class="PassengersInput-emptyStateText">
            {{ t('ui.booking.search.passengers.emptyState') }}
          </p>

          <ActionButton
            v-if="!hideAddNewPassenger"
            variant="secondary"
            size="sm"
            @click="$emit('add-new')"
          >
            {{ t('ui.booking.search.passengers.addNewPassenger') }}
          </ActionButton>
        </div>
      </div>

      <div class="PassengersInput-footer">
        <ActionButton
          variant="tertiary"
          size="sm"
          :disabled="hasSelectedAll"
          @click="selectAll"
        >
          {{ t('ui.booking.search.passengers.selectAll') }}
        </ActionButton>
      </div>
    </div>

    <template v-if="isMobile" #footer>
      <div class="PassengersInput-drawerFooter">
        <ActionButton
          variant="secondary"
          size="sm"
          class="PassengersInput-drawerFooterButton"
          @click="cancelSelection"
        >
          {{ t('ui.booking.search.passengers.cancel') }}
        </ActionButton>

        <ActionButton
          variant="primary"
          size="sm"
          class="PassengersInput-drawerFooterButton"
          :loading="evaluating"
          @click="confirmSelection"
        >
          {{ t('ui.booking.search.passengers.done') }}
        </ActionButton>
      </div>
    </template>
  </ResponsivePanel>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { useFieldValidation, useResponsive } from '@cvo/ui/composables';
import { getPersonFullName, type SeatType } from '@cvo/domain';
import ResponsivePanel from '../../organisms/ResponsivePanel/ResponsivePanel.vue';
import FieldLabel from '../../atoms/FieldLabel/FieldLabel.vue';
import FieldHelper from '../../atoms/FieldHelper/FieldHelper.vue';
import Avatar from '../../atoms/Avatar/Avatar.vue';
import Icon from '../../atoms/Icon/Icon.vue';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import Status from '../Status/Status.vue';
import type { PassengerEvaluationMessage } from '../../domain/SearchTrip/SearchTrip.model.ts';
import PassengerItem from './PassengerItem.vue';
import type { PassengersInputProps, PassengersInputEmits, PassengersInputSelection } from './PassengersInput.model';

const props = withDefaults(defineProps<PassengersInputProps>(), {
  passengers: () => [],
  sideOffset: undefined,
});

const emit = defineEmits<PassengersInputEmits>();

const selectedPassengers = defineModel<string[]>({ default: () => [] });
/** Explicit seat choices, keyed by person id. An absent entry means the API's default option. */
const seatTypes = defineModel<Record<string, SeatType>>('seatTypes', { default: () => ({}) });

const { t } = useI18n();
const { hasError, errorMessage, isRequired, hideAsterisk } = useFieldValidation(props);
const { isMobile } = useResponsive();

const isOpen = ref(false);
// eslint-disable-next-line vue/no-ref-object-reactivity-loss
const draftSelectedIds = ref<string[]>([...selectedPassengers.value]);
// eslint-disable-next-line vue/no-ref-object-reactivity-loss
const draftSeatTypes = ref<Record<string, SeatType>>({ ...seatTypes.value });

// On mobile only, updates the draft selection on opening.
watch(isOpen, (open) => {
  if (open && isMobile.value) {
    draftSelectedIds.value = [...selectedPassengers.value];
    draftSeatTypes.value = { ...seatTypes.value };
  }
});

// Messages and limits describe what the panel shows, so a closed drawer falls back to the committed value.
const displayedSelection = computed<PassengersInputSelection>(() => (isMobile.value && isOpen.value
  ? { passengerIds: draftSelectedIds.value, seatTypes: draftSeatTypes.value }
  : { passengerIds: selectedPassengers.value, seatTypes: seatTypes.value }));
watch(displayedSelection, selection => emit('evaluate', selection), { immediate: true });

// Single source of truth for the draft/committed choice: on mobile every read and write goes
// through the draft, elsewhere straight to the model.
const activeSelectedIds = computed({
  get: () => (isMobile.value ? draftSelectedIds.value : selectedPassengers.value),
  set: (ids) => {
    if (isMobile.value) draftSelectedIds.value = ids;
    else selectedPassengers.value = ids;
  },
});
const activeSeatTypes = computed({
  get: () => (isMobile.value ? draftSeatTypes.value : seatTypes.value),
  set: (types) => {
    if (isMobile.value) draftSeatTypes.value = types;
    else seatTypes.value = types;
  },
});

const activePassengers = computed(() => props.passengers.filter(passenger => activeSelectedIds.value.includes(passenger.person.id)));
const availablePassengers = computed(() => props.passengers.filter(passenger => !activeSelectedIds.value.includes(passenger.person.id)));
const noPassengersAvailable = computed(() => availablePassengers.value.length === 0 && activePassengers.value.length === 0);

// The trigger pill always reflects the committed value, never the in-progress draft.
const triggerSelectedPassengers = computed(() => props.passengers.filter(passenger => selectedPassengers.value.includes(passenger.person.id)));

const isMaxReached = computed(() => (props.maxPassengers ? activePassengers.value.length >= props.maxPassengers : false));
const hasSelectedAll = computed(() => isMaxReached.value || activePassengers.value.length === props.passengers.length);

/** Local code for the max-reached notice; never collides with a server code. */
const MAX_REACHED_CODE = 'MAX_PASSENGERS_REACHED';
/** The locally derived max-reached notice, ahead of the server-evaluated composition messages. */
const statusMessages = computed<PassengerEvaluationMessage[]>(() => [
  ...(isMaxReached.value ? [{ code: MAX_REACHED_CODE, message: t('ui.booking.search.passengers.maxReached'), status: 'info' as const }] : []),
  ...(props.messages ?? []),
]);

function togglePassenger(passengerId: string) {
  if (activeSelectedIds.value.includes(passengerId)) {
    const remaining = activeSelectedIds.value.filter(id => id !== passengerId);
    activeSelectedIds.value = remaining;
    // A removed passenger must not leave a stale seat choice behind.
    pruneSeatTypes(remaining);
    return;
  }
  if (isMaxReached.value) return;
  activeSelectedIds.value = [...activeSelectedIds.value, passengerId];
}

function selectAll() {
  const newPassengers = props.passengers.slice(0, props.maxPassengers).map(passenger => passenger.person.id);
  activeSelectedIds.value = newPassengers;
  pruneSeatTypes(newPassengers);
}

function pruneSeatTypes(selectedIds: string[]) {
  activeSeatTypes.value = Object.fromEntries(Object.entries(activeSeatTypes.value).filter(([id]) => selectedIds.includes(id)));
}

function setSeatSelection(passengerId: string, value: SeatType | undefined) {
  const next = { ...activeSeatTypes.value };
  if (value) next[passengerId] = value;
  else delete next[passengerId];
  activeSeatTypes.value = next;
}

function confirmSelection() {
  selectedPassengers.value = draftSelectedIds.value;
  seatTypes.value = draftSeatTypes.value;
  isOpen.value = false;
}

function cancelSelection() {
  isOpen.value = false;
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  // PanelDropdown's trigger wrapper has its own brand-blue background, which shows through
  // behind the label (the field itself is opaque, but the label row isn't). Neutralize it here
  // rather than in PanelDropdown, which is shared by unrelated triggers that rely on that color.
  .PanelDropdown-trigger:has(> .PassengersInput) {
    background-color: transparent;
  }

  .PassengersInput {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-2;
    width: 100%;
    max-width: 800px;
    min-width: 64px;

    &-field {
      display: flex;
      align-items: center;
      width: 100%;
      gap: vars.$dimensions-d-1;
      min-height: 29px;
      padding: vars.$atoms-autocomplete-baseinput-padding-top-bottom vars.$atoms-autocomplete-baseinput-padding-left-right;
      background-color: vars.$atoms-autocomplete-baseinput-enabled-bg-color;
      border: vars.$atoms-autocomplete-baseinput-border-stroke solid vars.$atoms-autocomplete-baseinput-enabled-border-color;
      border-radius: vars.$atoms-autocomplete-baseinput-border-radius;
      font: inherit;
      text-align: start;
      cursor: pointer;

      &:focus-visible {
        outline: 2px solid vars.$color-brand-focus;
        outline-offset: 2px;
      }
    }

    &.is-disabled &-field,
    &-field:disabled {
      cursor: not-allowed;
      background-color: vars.$atoms-autocomplete-baseinput-disabled-bg-color;
      border-color: vars.$atoms-autocomplete-baseinput-disabled-border-color;
    }

    &-content {
      display: flex;
      flex: 1 0 0;
      align-items: center;
      min-width: 0;
    }

    &-placeholder {
      @include text.text('sm');
      color: vars.$atoms-autocomplete-baseinput-placeholder-color;
    }

    &-avatars {
      display: flex;
      align-items: center;
    }

    &-avatar:not(:first-child) {
      margin-inline-start: -#{vars.$dimensions-d-1};
    }

    &-chevron {
      flex-shrink: 0;
      color: vars.$atoms-autocomplete-baseinput-enabled-icon-color;
    }

    &-drawerHeader {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
      width: 100%;
    }

    &-drawerHeaderLabel {
      @include text.text('sm', vars.$typography-weight-strong);
      color: vars.$atoms-input-enabled-label-color;
    }

    &-drawerFooter {
      display: flex;
      gap: vars.$dimensions-d-3;
      width: 100%;
    }

    &-drawerFooterButton {
      flex: 1;
    }

    &-panel {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-3;
      min-width: 200px; // Arbitraty value to avoid too shrinked dropdown on some viewport sizes.
    }

    &-section {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
      width: 100%;
    }

    // Sections preceeded by section have top border separator
    &-section ~ &-section {
      padding-block-start: vars.$dimensions-d-3;
      border-block-start: 1px solid vars.$color-neutral-gray-300;
    }

    &-sectionTitle {
      @include text.text('md', vars.$typography-weight-strong);
      color: vars.$color-neutral-gray-900;
    }

    &-list {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
      width: 100%;

      // Only the entering item animates; a removed item just disappears immediately, so toggling
      // one off never fights with the one appearing on the other side.
      &-enter-active {
        transition: opacity vars.$transition-base ease-out;
      }

      &-enter-from {
        opacity: 0;
      }

      &-leave-active {
        transition: none;
      }
    }

    &-emptyState {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: vars.$dimensions-d-3;
    }

    &-emptyStateText {
      @include text.text('xs');
      color: vars.$color-neutral-gray-800;
      overflow-wrap: break-word;
    }

    &-footer {
      display: flex;
      justify-content: flex-end;
      padding-block-start: vars.$dimensions-d-2;
      border-block-start: 1px solid vars.$color-neutral-gray-300;
    }
  }
}
</style>
